import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Typography, Box, Alert, Button, Icon, Card } from "@mui/material";

import DashboardLayout from "../LayoutContainers/DashboardLayout";
import DashboardNavbar from "../Navbars/DashboardNavbar";
import Footer from "../Footer";
import MDBox from "../MDBox";
import Api from "../../Api/Api";
import colors from "../../colors";
import { useMaterialUIController } from "../../context";
import { setActiveQuiz, useUserController } from "../../context/user";
import {useNavigate} from "react-router-dom";

const VideoPlayer = () => {
  const { id } = useParams();
  const videoRef = useRef(null);
  const intervalRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [videoLog, setVideoLog] = useState(null);
  const [quiz, setQuiz] = useState(null);
  const [finished, setFinished] = useState(null);
  const [resumed, setResumed] = useState(false);
  const [play, setPlay] = useState(false);
  const startTime = useRef(null);

  const [controller] = useMaterialUIController();
  const [userController, userDispatch] = useUserController();
  const { user } = userController;
  const { spanish } = controller;

  let navigate = useNavigate();

  useEffect(() => {
    if (id && user && !videoLog) {
      Api.getVideoLog(user.id, id)
        .then(({ data }) => setVideoLog(data || {}))
        .catch(console.error);
    }
  }, [id, user, videoLog]);

  useEffect(() => {
    if (id && !quiz) {
      Api.getOneQuiz(id).then(({ data }) => {
        const q = data[0];
        const vid = spanish && q.spanish ? q.spanish : q.english;
        setQuiz(q);
        setVideo({ url: vid.video, title: vid.title });
      });
    }
  }, [id, spanish, quiz]);

  useEffect(() => {
    if (quiz) {
      const vid = spanish && quiz.spanish ? quiz.spanish : quiz.english;
      setVideo({ url: vid.video, title: vid.title });
    }
  }, [spanish, quiz]);

  const playPause = () => {
    const el = videoRef.current;
    setPlay(prev => {
      const newPlay = !prev;
      if (newPlay) {
        if (!startTime.current) startTime.current = new Date();
        el.play();
        setActiveQuiz(userDispatch, {
          id,
          videoId: id,
          userId: user.id,
          videoName: video.title,
          videoStartTime: startTime.current,
        });
      } else {
        el.pause();
        setActiveQuiz(userDispatch, {
          id,
          videoId: id,
          userId: user.id,
          videoPauseTime: new Date(),
          lastVideoPausedTime: el.currentTime,
        });
      }
      return newPlay;
    });
  };

  const videoFinished = () => {
    setFinished(true);
    setActiveQuiz(userDispatch, {
      id,
      videoId: id,
      userId: user.id,
      videoEndTime: new Date(),
      ended: true
    });
  };

  const skipTo = (timeInSeconds) => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    const seek = () => {
      videoEl.currentTime = timeInSeconds;
      videoEl.removeEventListener("loadedmetadata", seek);
    };
    if (videoEl.readyState < 1) {
      videoEl.addEventListener("loadedmetadata", seek);
      videoEl.load();
    } else {
      videoEl.currentTime = timeInSeconds;
    }
  };

  const takeQuiz = () => {
    navigate(`/quiz?quizId=${id}`)
  };

  const fullScreen = () => {
    const el = videoRef.current;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  const iOS = () => {
    const platform = navigator.platform;
    return /iPhone|iPad|iPod/.test(platform) || (navigator.userAgent.includes("Mac") && "ontouchend" in document);
  };

  const sendPlaybackPing = () => {
    const el = videoRef.current;
    setActiveQuiz(userDispatch, {
      id,
      videoId: id,
      userId: user.id,
      videoPauseTime: new Date(),
      lastVideoPausedTime: el.currentTime,
    });
  };

  const startInterval = () => {
    if (!intervalRef.current) intervalRef.current = setInterval(sendPlaybackPing, 5000);
  };

  const stopInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    const el = videoRef.current;
    if (el) {
      el.addEventListener("play", startInterval);
      el.addEventListener("pause", stopInterval);
      el.addEventListener("ended", stopInterval);
      return () => {
        el.removeEventListener("play", startInterval);
        el.removeEventListener("pause", stopInterval);
        el.removeEventListener("ended", stopInterval);
        stopInterval();
      };
    }
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={0}>
        <Card>
          <Alert severity="warning">
            {spanish
              ? "Este video no se puede adelantar ni omitir. Debe verlo completo para continuar."
              : "This video cannot be skipped or fast-forwarded. Please watch it in full to proceed."}
          </Alert>
          <Alert severity="info">
            {spanish ? "Instrucciones" : "Instructions"}
            <ul style={{ paddingLeft: 25 }}>
              <li>{spanish ? "Haz clic en Iniciar para ver el video completo." : "Click Start to watch the full video."}</li>
              <li>{spanish ? "Después del video, podrás acceder al cuestionario." : "After the video, you will be able to access the quiz."}</li>
              <li>{spanish ? "Se requiere un 70% o más para aprobar y recibir tu certificado." : "A 70% score or higher is required to pass and receive your certificate."}</li>
            </ul>
          </Alert>
          <MDBox p={3} lineHeight={1} height="72vh" sx={{ overflow: "scroll", display: "flex", justifyContent: "flex-start", alignItems: "center", flexDirection: "column", paddingTop: 6 }}>
            <Box maxWidth="1200px">
              {video && (
                <>
                  <Typography>{video.title}</Typography>
                  <Box component="section" sx={{ maxWidth: "700px", marginTop: 1, marginBottom: 4 }}>
                    <video
                      ref={videoRef}
                      src={video.url}
                      playsInline
                      controls
                      width="100%"
                      disablePictureInPicture
                      onEnded={videoFinished}
                      onContextMenu={e => e.preventDefault()}
                      controlsList="nodownload nofullscreen noremoteplayback"
                    />
                    <Box sx={{ border: "1px solid #bababc", display: "flex", justifyContent: "space-between", alignItems: "center", padding: 1, backgroundColor: colors.Theme.lightBlue }}>
                      <Button variant="contained" onClick={playPause}>
                        <Icon fontSize="large" style={{ marginRight: 5 }}>{play ? "pause" : "play_arrow"}</Icon>
                        {spanish ? (play ? "Pausar video" : "Reproducir video") : (play ? "Pause Video" : "Play Video")}
                      </Button>
                      {(finished || videoLog?.ended) && (
                        <Button variant="contained" onClick={takeQuiz}>
                          {spanish ? "Iniciar cuestionario" : "Take Quiz"}
                        </Button>
                      )}
                      {!iOS() && (
                        <Button variant="contained" onClick={fullScreen}>
                          <Icon fontSize="large" style={{ marginRight: 5 }}>fullscreen</Icon>
                          {spanish ? "Pantalla completa" : "Full Screen"}
                        </Button>
                      )}
                    </Box>
                    {videoLog?.ended && (
                      <Alert severity="success" sx={{ marginTop: 1 }}>
                        {spanish
                          ? "Ya has visto este video. Puedes volver a verlo o continuar con el cuestionario."
                          : "You have already completed this video. You may rewatch it or proceed to the quiz."}
                      </Alert>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default VideoPlayer;
